/**
 * WAVE5-W1 — klient HTTP dla dziennej nagrody (status + claim).
 */
import {
  DailyClaimResponseSchema,
  DailyStatusResponseSchema,
  type DailyClaimResponse,
  type DailyStatusResponse,
  ApiErrorSchema,
} from '@pieskowo/shared';
import { apiBase, authHeaders } from './apiBase.js';

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; code: string; retryable: boolean; status: number };

async function parseError(res: Response): Promise<ApiResult<never>> {
  try {
    const json: unknown = await res.json();
    const err = ApiErrorSchema.safeParse(json);
    if (err.success) {
      return {
        ok: false,
        code: err.data.error.code,
        retryable: err.data.error.retryable,
        status: res.status,
      };
    }
  } catch {
    /* ignore */
  }
  return {
    ok: false,
    code: res.status === 401 ? 'UNAUTHORIZED' : 'INTERNAL',
    retryable: res.status >= 500,
    status: res.status,
  };
}

/** GET /api/economy/daily/status */
export async function fetchDailyStatus(): Promise<ApiResult<DailyStatusResponse>> {
  try {
    const res = await fetch(`${apiBase()}/api/economy/daily/status`, {
      method: 'GET',
      headers: authHeaders(),
    });
    if (!res.ok) return parseError(res);
    const json: unknown = await res.json();
    const out = DailyStatusResponseSchema.safeParse(json);
    if (!out.success) {
      return { ok: false, code: 'VALIDATION', retryable: false, status: res.status };
    }
    return { ok: true, data: out.data };
  } catch {
    return { ok: false, code: 'INTERNAL', retryable: true, status: 0 };
  }
}

/** POST /api/economy/daily/claim */
export async function claimDailyReward(): Promise<ApiResult<DailyClaimResponse>> {
  try {
    const res = await fetch(`${apiBase()}/api/economy/daily/claim`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({}),
    });
    if (!res.ok) return parseError(res);
    const json: unknown = await res.json();
    const out = DailyClaimResponseSchema.safeParse(json);
    if (!out.success) {
      return { ok: false, code: 'VALIDATION', retryable: false, status: res.status };
    }
    return { ok: true, data: out.data };
  } catch {
    return { ok: false, code: 'INTERNAL', retryable: true, status: 0 };
  }
}
